import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { EntityThumbnail } from '../components/EntityImage';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import {
  Target,
  Boxes,
  Wrench,
  FileText,
  Heart,
  Plus,
  ArrowRight,
  Shield,
  DollarSign,
  Eye,
  EyeOff,
  TrendingUp,
} from 'lucide-react';

// Storage key for privacy toggle
const PRIVACY_TOGGLE_KEY = 'armsroom.dashboard.hideValue';

// Custom tooltip component for chart - defined outside to avoid re-renders
const ChartTooltip = ({ active, payload, hideValue, formatCurrency }) => {
  if (active && payload && payload.length) {
    const data = payload[0];
    return (
      <div className="bg-card border border-border rounded-sm px-3 py-2 shadow-lg">
        <p className="text-sm font-medium text-foreground">{data.payload.name}</p>
        <p className="text-sm text-muted-foreground">
          {hideValue ? '•••••' : formatCurrency(data.value)}
        </p>
      </div>
    );
  }
  return null;
};

export default function DashboardPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recentItems, setRecentItems] = useState(null);
  const [collectionValue, setCollectionValue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [valueLoading, setValueLoading] = useState(true);
  const [hideValue, setHideValue] = useState(() => {
    // Default to hidden (true) unless explicitly set to 'false'
    const stored = localStorage.getItem(PRIVACY_TOGGLE_KEY);
    return stored !== 'false';
  });

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const [statsRes, recentRes] = await Promise.all([
          api.get('/dashboard/stats'),
          api.get('/dashboard/recent'),
        ]);
        setStats(statsRes.data);
        setRecentItems(recentRes.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      } finally {
        setLoading(false);
      }
    };

    const fetchCollectionValue = async () => {
      try {
        const response = await api.get('/dashboard/collection-value');
        setCollectionValue(response.data);
      } catch (error) {
        console.error('Error fetching collection value:', error);
      } finally {
        setValueLoading(false);
      }
    };

    fetchDashboardData();
    fetchCollectionValue();
  }, []);

  // Toggle privacy and persist to localStorage
  const toggleValueVisibility = () => {
    const newValue = !hideValue;
    setHideValue(newValue);
    localStorage.setItem(PRIVACY_TOGGLE_KEY, newValue ? 'true' : 'false');
  };

  // Format currency
  const formatCurrency = (value) => {
    if (value == null) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Prepare chart data
  const getChartData = () => {
    if (!collectionValue?.breakdown) return [];
    
    return [
      {
        name: 'Firearms',
        value: collectionValue.breakdown.firearms || 0,
        color: '#8B5CF6', // Purple/secondary
      },
      {
        name: 'Ammo',
        value: collectionValue.breakdown.ammunition || 0,
        color: '#F59E0B', // Amber/accent
      },
      {
        name: 'Accessories',
        value: collectionValue.breakdown.accessories || 0,
        color: '#6366F1', // Indigo
      },
    ];
  };

  const chartData = getChartData();

  const statCards = [
    { label: 'Firearms', value: stats?.firearms || 0, icon: Target, color: 'text-secondary', path: '/firearms' },
    { label: 'Total Rounds', value: stats?.ammunition_rounds || 0, icon: Boxes, color: 'text-accent', path: '/ammunition' },
    { label: 'Accessories', value: stats?.accessories || 0, icon: Wrench, color: 'text-secondary', path: '/accessories' },
    { label: 'Documents', value: stats?.documents || 0, icon: FileText, color: 'text-muted-foreground', path: '/documents' },
    { label: 'Wishlist', value: stats?.wishlist || 0, icon: Heart, color: 'text-destructive', path: '/wishlist' },
  ];

  const quickActions = [
    { label: 'Add Firearm', icon: Target, path: '/firearms/new', color: 'bg-[#1C2951] hover:bg-[#243663] text-white' },
    { label: 'Add Ammo', icon: Boxes, path: '/ammunition/new', color: 'bg-[#2E4A7D] hover:bg-[#3B5998] text-white' },
    { label: 'Add Accessory', icon: Wrench, path: '/accessories/new', color: 'bg-[#4A6FA5] hover:bg-[#5A7FB5] text-white' },
    { label: 'Add Document', icon: FileText, path: '/documents/new', color: 'bg-[#1A365D] hover:bg-[#234876] text-white' },
  ];

  // Tooltip renderer for chart
  const renderTooltip = (props) => (
    <ChartTooltip {...props} hideValue={hideValue} formatCurrency={formatCurrency} />
  );

  return (
    <Layout title="Dashboard">
      <div className="space-y-8" data-testid="dashboard-page">
        {/* Welcome Section */}
        <div className="bg-card border border-border rounded-sm p-6">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-foreground font-['Chivo'] uppercase tracking-tight mb-1">
                Welcome to Arms Room
              </h2>
              <p className="text-muted-foreground">
                Manage your firearms inventory from here. Keep your collection organized and documented.
              </p>
            </div>
            <div className="hidden sm:flex items-center gap-2 p-3 bg-muted rounded-sm">
              <Shield className="w-5 h-5 text-secondary" />
              <span className="text-xs uppercase tracking-widest text-muted-foreground font-mono">Secured</span>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
          {statCards.map((stat, index) => (
            <Card
              key={stat.label}
              className="bg-card border-border rounded-sm cursor-pointer card-hover"
              onClick={() => navigate(stat.path)}
              data-testid={`stat-${stat.label.toLowerCase().replace(' ', '-')}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <stat.icon className={`w-5 h-5 ${stat.color}`} />
                  <ArrowRight className="w-4 h-4 text-muted-foreground" />
                </div>
                <div>
                  <p className={`text-3xl font-bold ${loading ? 'animate-pulse bg-muted rounded w-12 h-8' : ''}`}>
                    {loading ? '' : stat.value.toLocaleString()}
                  </p>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{stat.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Quick Actions */}
        <div>
          <h3 className="text-sm uppercase tracking-widest text-muted-foreground mb-4 font-['Chivo']">
            Quick Actions
          </h3>
          <div className="flex flex-wrap gap-4 justify-center">
            {quickActions.map((action) => (
              <Button
                key={action.label}
                onClick={() => navigate(action.path)}
                className={`${action.color} rounded-sm btn-tactical px-6 py-6 text-base`}
                data-testid={`quick-action-${action.label.toLowerCase().replace(' ', '-')}`}
              >
                <action.icon className="w-5 h-5 mr-2" />
                {action.label}
              </Button>
            ))}
          </div>
        </div>

        {/* Recently Added */}
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Recent Firearms */}
          <Card className="bg-card border-border rounded-sm">
            <CardHeader className="border-b border-border/50 p-4">
              <CardTitle className="text-sm uppercase tracking-widest font-['Chivo'] flex items-center gap-2">
                <Target className="w-4 h-4 text-secondary" />
                Recent Firearms
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-12 bg-muted animate-pulse rounded-sm" />
                  ))}
                </div>
              ) : recentItems?.firearms?.length > 0 ? (
                <div className="space-y-3">
                  {recentItems.firearms.map((firearm) => (
                    <div
                      key={firearm.id}
                      className="flex items-center gap-3 p-2 hover:bg-muted rounded-sm cursor-pointer transition-colors"
                      onClick={() => navigate(`/firearms/${firearm.id}`)}
                      data-testid={`recent-firearm-${firearm.id}`}
                    >
                      <EntityThumbnail
                        imageFileIds={firearm.images}
                        alt=""
                        containerClassName="w-10 h-10 bg-muted rounded-sm flex items-center justify-center overflow-hidden"
                        className="w-full h-full object-cover"
                        fallback={<Target className="w-5 h-5 text-muted-foreground" />}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{firearm.manufacturer} {firearm.model}</p>
                        <p className="text-xs text-muted-foreground">{firearm.caliber}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Target className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No firearms yet</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    onClick={() => navigate('/firearms/new')}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add First
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Ammunition */}
          <Card className="bg-card border-border rounded-sm">
            <CardHeader className="border-b border-border/50 p-4">
              <CardTitle className="text-sm uppercase tracking-widest font-['Chivo'] flex items-center gap-2">
                <Boxes className="w-4 h-4 text-accent" />
                Recent Ammunition
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-12 bg-muted animate-pulse rounded-sm" />
                  ))}
                </div>
              ) : recentItems?.ammunition?.length > 0 ? (
                <div className="space-y-3">
                  {recentItems.ammunition.map((ammo) => (
                    <div
                      key={ammo.id}
                      className="flex items-center gap-3 p-2 hover:bg-muted rounded-sm cursor-pointer transition-colors"
                      onClick={() => navigate(`/ammunition/${ammo.id}`)}
                      data-testid={`recent-ammo-${ammo.id}`}
                    >
                      <EntityThumbnail
                        imageFileIds={ammo.images}
                        alt=""
                        containerClassName="w-10 h-10 bg-accent/10 rounded-sm flex items-center justify-center overflow-hidden"
                        className="w-full h-full object-cover"
                        fallback={<Boxes className="w-5 h-5 text-accent" />}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{ammo.caliber}</p>
                        <p className="text-xs text-muted-foreground">{ammo.brand || 'Unknown brand'}</p>
                      </div>
                      <span className="text-sm font-mono font-medium">{ammo.rounds}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Boxes className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No ammunition yet</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    onClick={() => navigate('/ammunition/new')}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add First
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Recent Accessories */}
          <Card className="bg-card border-border rounded-sm">
            <CardHeader className="border-b border-border/50 p-4">
              <CardTitle className="text-sm uppercase tracking-widest font-['Chivo'] flex items-center gap-2">
                <Wrench className="w-4 h-4 text-secondary" />
                Recent Accessories
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4">
              {loading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="h-12 bg-muted animate-pulse rounded-sm" />
                  ))}
                </div>
              ) : recentItems?.accessories?.length > 0 ? (
                <div className="space-y-3">
                  {recentItems.accessories.map((accessory) => (
                    <div
                      key={accessory.id}
                      className="flex items-center gap-3 p-2 hover:bg-muted rounded-sm cursor-pointer transition-colors"
                      onClick={() => navigate(`/accessories/${accessory.id}`)}
                      data-testid={`recent-accessory-${accessory.id}`}
                    >
                      <EntityThumbnail
                        imageFileIds={accessory.images}
                        alt=""
                        containerClassName="w-10 h-10 bg-muted rounded-sm flex items-center justify-center overflow-hidden"
                        className="w-full h-full object-cover"
                        fallback={<Wrench className="w-5 h-5 text-muted-foreground" />}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{accessory.name}</p>
                        <p className="text-xs text-muted-foreground">{accessory.type}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-6">
                  <Wrench className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No accessories yet</p>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="mt-2"
                    onClick={() => navigate('/accessories/new')}
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add First
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Collection Value Card */}
        <Card className="bg-card border-border rounded-sm" data-testid="collection-value-card">
          <CardHeader className="border-b border-border/50 p-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm uppercase tracking-widest font-['Chivo'] flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-secondary" />
                Collection Value
              </CardTitle>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">
                  {hideValue ? 'Hidden' : 'Visible'}
                </span>
                <button
                  onClick={toggleValueVisibility}
                  className="p-1.5 hover:bg-muted rounded-sm transition-colors"
                  aria-label={hideValue ? 'Show value' : 'Hide value'}
                  data-testid="toggle-value-visibility"
                >
                  {hideValue ? (
                    <EyeOff className="w-4 h-4 text-muted-foreground" />
                  ) : (
                    <Eye className="w-4 h-4 text-secondary" />
                  )}
                </button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-6">
            {valueLoading ? (
              <div className="space-y-4">
                <div className="h-10 bg-muted animate-pulse rounded-sm w-48" />
                <div className="h-48 bg-muted animate-pulse rounded-sm" />
              </div>
            ) : (
              <div className="space-y-6">
                {/* Total Value Display */}
                <div className="flex items-center gap-4">
                  <div className="w-14 h-14 bg-secondary/10 rounded-sm flex items-center justify-center">
                    <DollarSign className="w-7 h-7 text-secondary" />
                  </div>
                  <div>
                    <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">
                      Total Estimated Value
                    </p>
                    <p className="text-4xl font-bold font-mono text-foreground" data-testid="total-value">
                      {hideValue ? '•••••••' : formatCurrency(collectionValue?.total || 0)}
                    </p>
                  </div>
                </div>

                {/* Breakdown Mini Stats */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-muted/30 rounded-sm text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Target className="w-4 h-4 text-[#8B5CF6]" />
                      <span className="text-xs text-muted-foreground">Firearms</span>
                    </div>
                    <p className="text-lg font-mono font-semibold">
                      {hideValue ? '•••' : formatCurrency(collectionValue?.breakdown?.firearms || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-sm text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Boxes className="w-4 h-4 text-[#F59E0B]" />
                      <span className="text-xs text-muted-foreground">Ammo</span>
                    </div>
                    <p className="text-lg font-mono font-semibold">
                      {hideValue ? '•••' : formatCurrency(collectionValue?.breakdown?.ammunition || 0)}
                    </p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-sm text-center">
                    <div className="flex items-center justify-center gap-1 mb-1">
                      <Wrench className="w-4 h-4 text-[#6366F1]" />
                      <span className="text-xs text-muted-foreground">Accessories</span>
                    </div>
                    <p className="text-lg font-mono font-semibold">
                      {hideValue ? '•••' : formatCurrency(collectionValue?.breakdown?.accessories || 0)}
                    </p>
                  </div>
                </div>

                {/* Bar Chart */}
                <div className="h-48">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="vertical" margin={{ left: 0, right: 20 }}>
                      <XAxis 
                        type="number" 
                        hide 
                        domain={[0, 'dataMax']}
                      />
                      <YAxis 
                        type="category" 
                        dataKey="name" 
                        axisLine={false}
                        tickLine={false}
                        tick={{ fill: 'hsl(var(--muted-foreground))', fontSize: 12 }}
                        width={80}
                      />
                      <Tooltip content={renderTooltip} cursor={{ fill: 'hsl(var(--muted))', opacity: 0.3 }} />
                      <Bar 
                        dataKey="value" 
                        radius={[0, 4, 4, 0]}
                        maxBarSize={40}
                      >
                        {chartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                {/* Note about values */}
                <p className="text-xs text-muted-foreground text-center">
                  Based on current values for firearms, purchase prices for accessories, and calculated ammo value (cost per round × current rounds).
                  {collectionValue?.total === 0 && ' Add values to your items to see totals.'}
                </p>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
